import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ALLOWED_FILE_TYPES } from '@avanta/shared/constants/chat';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { AttachmentList } from './components/AttachmentList';
import { AudioPreview } from './components/AudioPreview';
import { MessageInputRow } from './components/MessageInputRow';
import { RecordingUI } from './components/RecordingUI';
import { useChatInput } from './hooks/useChatInput';
import { type InputChatProps } from './types';

export type { InputChatProps, InputChatSendPayload } from './types';

export const InputChat = ({
  leadingActions,
  onAfterSend,
}: InputChatProps = {}): React.ReactElement => {
  const {
    methods,
    selectedFiles,
    fileInputRef,
    isSending,
    isRecording,
    audioFile,
    recordingTime,
    handleSendMessage,
    handleMessageChange,
    handleFileChange,
    handleRemoveFile,
    handleStartRecording,
    handleAttachClick,
  } = useChatInput({ onAfterSend });
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;
  const { t } = useTranslation(LOCIZE_NS.CHAT);

  return (
    <FormProvider {...methods}>
      <form
        data-chat-form
        onSubmit={handleSubmit(handleSendMessage)}
        className="w-full border-t border-plum-200 px-2.5 py-2 max-am-xl:p-2 bg-plum-100 shrink-0 am-xl:block sticky bottom-0 left-0 right-0 z-10"
      >
        <div className="flex flex-col gap-2 w-full">
          {selectedFiles.length > 0 && !audioFile ? (
            <AttachmentList files={selectedFiles} onRemove={handleRemoveFile} />
          ) : null}
          <div className="flex gap-2 w-full justify-between">
            <input
              ref={fileInputRef}
              className="hidden"
              accept={Object.values(ALLOWED_FILE_TYPES).join(',')}
              type="file"
              multiple
              onChange={handleFileChange}
            />
            {isRecording ? (
              <RecordingUI
                isRecording={isRecording}
                isSending={isSending}
                recordingTime={recordingTime}
                recordingLabel={t('Chat.Messages.states.recording')}
                onRecordClick={handleStartRecording}
              />
            ) : audioFile ? (
              <AudioPreview
                audioFile={audioFile}
                isSubmitting={isSubmitting}
                onRemove={() => handleRemoveFile(audioFile.lastModified)}
              />
            ) : (
              <MessageInputRow
                selectedFilesCount={selectedFiles.length}
                isRecording={isRecording}
                isSending={isSending}
                isSubmitting={isSubmitting}
                leadingActions={leadingActions}
                onAttachClick={handleAttachClick}
                onMessageChange={handleMessageChange}
                onRecordClick={handleStartRecording}
              />
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
